import {
  CreateWorkflowDto,
  FluentsActionKey,
  Workflow,
  WorkflowActionProvider,
  WorkflowDataSourceConfigDto,
  WorkflowDataSourceEventType,
  WorkflowDataSourceProvider,
  WorkflowPostActionProcessorActionType,
  WorkflowPostActionProcessorConfigDto,
  WorkflowStatus,
  WorkflowStepDto,
  WorkflowStepType,
  WorkflowWithStatus,
} from 'src/interfaces/workflows.interface';
import { IntegrationAppActionKey } from '../integration-app.service';
import {
  EmailCampaignConfig,
  CrmCampaignConfig,
  SmsCampaignConfig,
  InitialCallCommunicationConfig,
  CommunicationChannel,
  CallCampaignConfig,
  Campaign,
  FollowUpCommunicationConfig,
  CampaignValidationError,
  UpdateCampaignDto,
} from './campaigns.interface';
import { formatPhoneNumberWithError } from 'src/utils/number';
import { ContactRecordField } from '../integration-app.service';

export const WORKFLOW_PARAM_PREFIX = 'params.context.';
export const WORKFLOW_TO_NUMBER_PARAM = 'to_number';
export const WORKFLOW_TO_EMAIL_PARAM = 'to_email';
export const AUTOMATIC_VARIABLE_PREFIXES = ['previous.'];

export enum EmailActionParams {
  TO = 'to',
  FROM = 'from',
  SUBJECT = 'subject',
  BODY = 'body',
}

export enum SmsActionParams {
  TO = 'to',
  FROM = 'from',
  BODY = 'body',
}

const CRM_PROVIDER_FIELD_MAPPING = {
  [WORKFLOW_TO_NUMBER_PARAM]: `{{event.fields.${ContactRecordField.PRIMARY_PHONE}}}`,
  [WORKFLOW_TO_EMAIL_PARAM]: `{{event.fields.${ContactRecordField.PRIMARY_EMAIL}}}`,
};

export const CAMPAIGN_STEP_REFERENCE_ID_PREFIX = '$fluents-step:campaigns.v1:';

export type CampaignWorkflowContext = {
  from_numbers: string[];

  emailCampaignConfig?: EmailCampaignConfig;
  crmCampaignConfig?: CrmCampaignConfig;
  smsCampaignConfig?: SmsCampaignConfig;
  initialCallConfig?: InitialCallCommunicationConfig;
};

export function findStep(workflow: Workflow, stepReferenceId: string) {
  return workflow.config.steps.find(
    (step) => step.reference_id === stepReferenceId,
  );
}

export function decodePauseStepDuration(step: WorkflowStepDto): number {
  if (step.type !== WorkflowStepType.PAUSE) {
    throw new Error(
      `decodePauseStepDuration: step ${step.reference_id} is not a PAUSE step`,
    );
  }

  const resumeAtExpression = step.params.resume_at;
  if (typeof resumeAtExpression !== 'string') {
    throw new Error(
      `decodePauseStepDuration: expected resume_at to be a string, got ${typeof resumeAtExpression}`,
    );
  }

  // Extract the JSON payload inside {{after( … )}}
  const m = resumeAtExpression.match(/{{\s*after\(\s*({[\s\S]*})\s*\)\s*}}/);
  if (!m) {
    throw new Error(
      `decodePauseStepDuration: invalid resume_at format: ${resumeAtExpression}`,
    );
  }

  try {
    const cfg = JSON.parse(m[1]);
    const secs = Number(cfg.secs);
    if (!Number.isFinite(secs)) {
      throw new Error(`secs is not a number: ${cfg.secs}`);
    }
    return secs;
  } catch (err) {
    throw new Error(
      `decodePauseStepDuration: failed to parse secs from ${m[1]} — ${err}`,
    );
  }
}
// Helper function to build step reference IDs

export function buildStepReferenceId(
  stepNumber: number,
  stepType: WorkflowStepType,
  communicationChannel: CommunicationChannel = CommunicationChannel.CALL,
) {
  return `${CAMPAIGN_STEP_REFERENCE_ID_PREFIX}${communicationChannel}-${stepType}-${stepNumber}`;
}

export const convertWorkflowParamsToUiTemplateParams = (
  text: string | undefined,
): string => {
  if (!text) return '';
  const workflowParamPrefixEscaped = WORKFLOW_PARAM_PREFIX.replace(
    /\./g,
    '\\.',
  );
  // 1) Convert {{ params.context.X }} -> { X }
  let result = text.replace(
    new RegExp(`{{(\\s*)${workflowParamPrefixEscaped}([^}]+)}}`, 'g'),
    '{$1$2}',
  );
  // 2) Convert {{ <automatic-prefix>.Y }} -> { <automatic-prefix>.Y }
  const automaticPrefixesPattern = AUTOMATIC_VARIABLE_PREFIXES.map((p) =>
    p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  ).join('|');
  if (automaticPrefixesPattern) {
    const automaticRegex = new RegExp(
      `{{(\\s*)(${automaticPrefixesPattern}[^}]+)}}`,
      'g',
    );
    result = result.replace(automaticRegex, '{$1$2}');
  }
  return result;
};

export const convertUiTemplateParamsToWorkflowParams = (
  text: string | undefined,
): string => {
  if (!text) return '';
  // If the captured variable starts with "<automatic-prefix>", do not prepend WORKFLOW_PARAM_PREFIX
  return text.replace(/\{\s*([^}\s]+)\s*\}/g, (_match, varName) => {
    const variable = String(varName);
    if (
      AUTOMATIC_VARIABLE_PREFIXES.some((prefix) => variable.startsWith(prefix))
    ) {
      return `{{${variable}}}`;
    }
    return `{{${WORKFLOW_PARAM_PREFIX}${variable}}}`;
  });
};

export const getUiTemplateParamsFromValue = (
  value: string | undefined,
): string[] => {
  if (!value) return [];
  const matches = value.match(new RegExp('{\\s*([^}\\s]+)\\s*}', 'g')) || [];
  return matches
    .map((match) => match.slice(1, -1).trim())
    .filter(
      (param) =>
        !AUTOMATIC_VARIABLE_PREFIXES.some((prefix) => param.startsWith(prefix)),
    );
};

export const buildCampaignFromWorkflow = (
  workflow: WorkflowWithStatus,
): Campaign => {
  const { config } = workflow;
  const { schedule } = config;
  const context = config.context as CampaignWorkflowContext;

  const startTime = schedule.allowedTimes[0].startTime;
  const endTime = schedule.allowedTimes[0].endTime;
  const days = schedule.allowedTimes.map((time) => time.day);
  const initialCallActionStep = findStep(
    workflow,
    buildStepReferenceId(1, WorkflowStepType.ACTION, CommunicationChannel.CALL),
  );

  let initialCallConfig = context.initialCallConfig;
  // Handle older workflows.
  if (!initialCallConfig) {
    initialCallConfig = {
      agentId: undefined as unknown as string,
      scheduleAfterHours: 0,
    };

    if (initialCallActionStep) {
      initialCallConfig.agentId = initialCallActionStep.params.agent as string;
    }

    const initialCallPauseStep = findStep(
      workflow,
      buildStepReferenceId(
        1,
        WorkflowStepType.PAUSE,
        CommunicationChannel.CALL,
      ),
    );
    if (initialCallPauseStep) {
      const pauseStepDurationSecs =
        decodePauseStepDuration(initialCallPauseStep);
      const scheduleAfterHours = Math.floor(pauseStepDurationSecs / 3600);
      initialCallConfig.scheduleAfterHours = scheduleAfterHours;
    }
  }

  let fromNumbers = context.from_numbers;
  // Handle older workflows.
  if (!fromNumbers && initialCallActionStep) {
    const stepParams = initialCallActionStep.params;
    fromNumbers = [stepParams.from_number as string];
  }
  const callConfig: CallCampaignConfig = {
    fromNumbers,
  };

  const followUpCommunicationConfigs: FollowUpCommunicationConfig[] = [];
  let skipFirst = true;
  let prevPauseStep = undefined;
  let daysAfter = 0;
  for (const step of config.steps) {
    if (step.type === WorkflowStepType.PAUSE) {
      prevPauseStep = step;
    } else if (step.type === WorkflowStepType.ACTION) {
      if (skipFirst) {
        skipFirst = false;
        continue;
      }

      const pauseStepDurationSecs = decodePauseStepDuration(prevPauseStep!);
      daysAfter += Math.floor(pauseStepDurationSecs / (24 * 60 * 60));

      // Check if this is a call, email, or SMS action
      const isEmailAction =
        step.config?.action_provider ===
          WorkflowActionProvider.INTEGRATION_APP &&
        step.config?.action_config?.action_key ===
          IntegrationAppActionKey.SEND_EMAIL;

      const isSmsAction =
        step.config?.action_provider ===
          WorkflowActionProvider.INTEGRATION_APP &&
        step.config?.action_config?.action_key ===
          IntegrationAppActionKey.SEND_MESSAGE;

      if (isEmailAction) {
        // This is an email step
        followUpCommunicationConfigs.push({
          scheduleAfterDays: daysAfter,
          contactVia: CommunicationChannel.EMAIL,
          emailConfig: {
            body: convertWorkflowParamsToUiTemplateParams(
              (step.params[EmailActionParams.BODY] as string) || '',
            ),
            subject: convertWorkflowParamsToUiTemplateParams(
              (step.params[EmailActionParams.SUBJECT] as string) || '',
            ),
          },
        });
      } else if (isSmsAction) {
        // This is an SMS step
        followUpCommunicationConfigs.push({
          scheduleAfterDays: daysAfter,
          contactVia: CommunicationChannel.SMS,
          smsConfig: {
            body: convertWorkflowParamsToUiTemplateParams(
              (step.params[SmsActionParams.BODY] as string) || '',
            ),
          },
        });
      } else {
        followUpCommunicationConfigs.push({
          scheduleAfterDays: daysAfter,
          contactVia: CommunicationChannel.CALL,
          callConfig: {
            agentId: step.params.agent as string,
          },
        });
      }
    }
  }

  let crmConfig = context.crmCampaignConfig;
  // Handle older workflows.
  if (
    !crmConfig &&
    workflow.config?.data_sources &&
    workflow.config?.data_sources.length > 0
  ) {
    const dataSources = workflow.config.data_sources;
    const crmDataSource = dataSources.find(
      (ds) => ds.source_provider === WorkflowDataSourceProvider.INTEGRATION_APP,
    );
    if (crmDataSource) {
      const crmProvider = crmDataSource.source_id;
      let recordCommunicationsInCrm = false;
      if (crmProvider) {
        recordCommunicationsInCrm = crmDataSource.post_action_processors.some(
          (processor) =>
            processor.action_type ===
            WorkflowPostActionProcessorActionType.RECORD_ACTION,
        );
      }
      crmConfig = {
        provider: crmProvider,
        recordCommunications: recordCommunicationsInCrm,
        newRecordFilter: crmDataSource.event_filter,
      };
    }
  }

  return {
    name: workflow.label,
    referenceId: workflow.referenceId,
    description: workflow.description || '',
    isActive: workflow.status === WorkflowStatus.WORKFLOW_ACTIVE,

    schedule: {
      campaignStartDate: schedule.startDate,
      days,
      startTime,
      endTime,
      timeZone: schedule.timeZone,
    },

    initialCallConfig,
    followUpCommunicationConfigs,

    callCampaignConfig: callConfig,
    emailCampaignConfig: context.emailCampaignConfig,
    smsCampaignConfig: context.smsCampaignConfig,
    crmCampaignConfig: crmConfig,

    status: workflow.status,
  };
};

function isTimeAfter(startTime: string, endTime: string): boolean {
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);

  if (startHours < endHours) {
    return true;
  } else if (startHours === endHours) {
    return startMinutes < endMinutes;
  }
  return false;
}

function buildPauseStep(
  callNumber: number,
  afterSecs: number,
): WorkflowStepDto {
  return {
    type: WorkflowStepType.PAUSE,
    label: 'Wait until the scheduled time',
    reference_id: buildStepReferenceId(
      callNumber,
      WorkflowStepType.PAUSE,
      CommunicationChannel.CALL,
    ),
    config: {},
    params: {
      resume_at: `{{after({"secs": ${afterSecs}})}}`,
    },
  };
}

function buildCallStep({
  followupNumber,
  agentId,
}: {
  followupNumber: number;
  agentId: string;
}): WorkflowStepDto {
  return {
    type: WorkflowStepType.ACTION,
    label: 'Make Call',
    reference_id: buildStepReferenceId(
      followupNumber,
      WorkflowStepType.ACTION,
      CommunicationChannel.CALL,
    ),
    config: {
      action_config: {
        action_key: FluentsActionKey.CREATE_CALL,
      },
      action_provider: WorkflowActionProvider.FLUENTS,
    },
    params: {
      agent: agentId,
      context: '{{params.context}}',
      to_number: `{{params.${WORKFLOW_TO_NUMBER_PARAM}}}`,
      from_number:
        '{{moduloSelect({"items":workflow.context.from_numbers,"value":execution.context.execution_number})}}',
    },
  };
}

export function buildEmailStep({
  followupNumber,
  fromEmail,
  subject,
  body,
  integrationKey,
}: {
  followupNumber: number;
  fromEmail: string;
  subject: string;
  body: string;
  integrationKey: string;
}): WorkflowStepDto {
  return {
    type: WorkflowStepType.ACTION,
    label: 'Send Email',
    reference_id: buildStepReferenceId(
      followupNumber,
      WorkflowStepType.ACTION,
      CommunicationChannel.EMAIL,
    ),
    config: {
      action_provider: WorkflowActionProvider.INTEGRATION_APP,
      action_config: {
        integration_key: integrationKey,
        action_key: IntegrationAppActionKey.SEND_EMAIL,
      },
    },
    params: {
      [EmailActionParams.SUBJECT]:
        convertUiTemplateParamsToWorkflowParams(subject),
      [EmailActionParams.BODY]: convertUiTemplateParamsToWorkflowParams(body),
      [EmailActionParams.TO]: `{{params.${WORKFLOW_TO_EMAIL_PARAM}}}`,
      [EmailActionParams.FROM]:
        convertUiTemplateParamsToWorkflowParams(fromEmail),
    },
  };
}

export function buildSmsStep({
  followupNumber,
  fromNumber,
  body,
  integrationKey,
}: {
  followupNumber: number;
  fromNumber: string;
  body: string;
  integrationKey: string;
}): WorkflowStepDto {
  return {
    type: WorkflowStepType.ACTION,
    label: 'Send SMS',
    reference_id: buildStepReferenceId(
      followupNumber,
      WorkflowStepType.ACTION,
      CommunicationChannel.SMS,
    ),
    config: {
      action_provider: WorkflowActionProvider.INTEGRATION_APP,
      action_config: {
        integration_key: integrationKey,
        action_key: IntegrationAppActionKey.SEND_MESSAGE,
      },
    },
    params: {
      [SmsActionParams.BODY]: convertUiTemplateParamsToWorkflowParams(body),
      [SmsActionParams.TO]: `{{params.${WORKFLOW_TO_NUMBER_PARAM}}}`,
      [SmsActionParams.FROM]:
        convertUiTemplateParamsToWorkflowParams(fromNumber),
    },
  };
}

export const buildWorkflowFromCampaign = (
  campaign: UpdateCampaignDto,
): CreateWorkflowDto => {
  const campaignStartTime = campaign.schedule.startTime;
  const campaignEndTime = campaign.schedule.endTime;

  if (!isTimeAfter(campaignStartTime, campaignEndTime)) {
    throw new CampaignValidationError('Start Time must be before End Time');
  }

  const initialCallAfterHours = campaign.initialCallConfig.scheduleAfterHours;
  const initialCallAfterSecs = initialCallAfterHours * 60 * 60;

  const timeZone = campaign.schedule.timeZone;

  let validatedSmsCampaignConfig = campaign.smsCampaignConfig;
  if (campaign.smsCampaignConfig?.fromNumber) {
    if (!campaign.smsCampaignConfig.fromNumber.trim()) {
      throw new CampaignValidationError(
        'From phone number is required for SMS communications',
      );
    }
    const { number, error: fromNumberError } = formatPhoneNumberWithError(
      campaign.smsCampaignConfig?.fromNumber,
    );
    if (fromNumberError) {
      throw new CampaignValidationError(`Invalid SMS from phone number`);
    }
    validatedSmsCampaignConfig = {
      ...campaign.smsCampaignConfig,
      fromNumber: number!,
    };
  }

  if (campaign.emailCampaignConfig?.fromEmail) {
    if (!campaign.emailCampaignConfig.fromEmail.trim()) {
      throw new CampaignValidationError('From email address is required');
    }
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(campaign.emailCampaignConfig.fromEmail)) {
      throw new CampaignValidationError(
        `Invalid from email address: ${campaign.emailCampaignConfig.fromEmail}`,
      );
    }
  }

  const allowedTimes = campaign.schedule.days.map((day) => ({
    day,
    startTime: campaignStartTime,
    endTime: campaignEndTime,
  }));

  const initialCallAgent = campaign.initialCallConfig.agentId;
  const fromNumbers = campaign.callCampaignConfig.fromNumbers;
  const steps = [
    buildPauseStep(1, initialCallAfterSecs),
    buildCallStep({
      followupNumber: 1,
      agentId: initialCallAgent,
    }),
  ];

  // Validate communication parameters
  const sortedFollowUpCommunications =
    campaign.followUpCommunicationConfigs.sort(
      (a, b) => a.scheduleAfterDays - b.scheduleAfterDays,
    );

  for (const communication of sortedFollowUpCommunications) {
    if (communication.contactVia === CommunicationChannel.EMAIL) {
      // Validate email subject and body
      if (!communication.emailConfig?.subject?.trim()) {
        throw new CampaignValidationError(
          'Email subject is required for all email communications',
        );
      }
      if (!communication.emailConfig?.body?.trim()) {
        throw new CampaignValidationError(
          'Email body is required for all email communications',
        );
      }
    } else if (communication.contactVia === CommunicationChannel.SMS) {
      if (!communication.smsConfig?.body?.trim()) {
        throw new CampaignValidationError(
          'SMS message is required for all SMS communications',
        );
      }
    }
  }

  // Then add the follow up communications to the steps
  let lastDaysAfter = 0;
  sortedFollowUpCommunications.forEach((communication, index) => {
    const deltaDays = communication.scheduleAfterDays - lastDaysAfter;
    lastDaysAfter = communication.scheduleAfterDays;
    steps.push(buildPauseStep(index + 2, deltaDays * 24 * 60 * 60));

    if (communication.contactVia === CommunicationChannel.CALL) {
      let followUpCommunicationAgent = communication.callConfig?.agentId;
      if (!followUpCommunicationAgent) {
        followUpCommunicationAgent = initialCallAgent;
      }

      steps.push(
        buildCallStep({
          followupNumber: index + 2,
          agentId: followUpCommunicationAgent,
        }),
      );
    } else if (communication.contactVia === CommunicationChannel.EMAIL) {
      const emailStep = buildEmailStep({
        followupNumber: index + 2,
        fromEmail: campaign.emailCampaignConfig?.fromEmail || '',
        subject: communication.emailConfig?.subject || '',
        body: communication.emailConfig?.body || '',
        integrationKey: campaign.emailCampaignConfig?.provider || '',
      });
      steps.push(emailStep);
    } else if (communication.contactVia === CommunicationChannel.SMS) {
      const smsStep = buildSmsStep({
        followupNumber: index + 2,
        fromNumber: campaign.smsCampaignConfig?.fromNumber || '',
        body: communication.smsConfig?.body || '',
        integrationKey: campaign.smsCampaignConfig?.provider || '',
      });
      steps.push(smsStep);
    }
  });

  const dataSources: WorkflowDataSourceConfigDto[] = [];
  if (campaign.crmCampaignConfig?.provider) {
    const postActionProcessors: WorkflowPostActionProcessorConfigDto[] = [];
    if (campaign.crmCampaignConfig?.recordCommunications) {
      postActionProcessors.push({
        action_type: WorkflowPostActionProcessorActionType.RECORD_ACTION,
      });
    }

    dataSources.push({
      source_provider: WorkflowDataSourceProvider.INTEGRATION_APP,
      source_id: campaign.crmCampaignConfig?.provider,
      event_types: [WorkflowDataSourceEventType.RECORD_CREATED],
      event_filter: campaign.crmCampaignConfig?.newRecordFilter,
      field_mapping: CRM_PROVIDER_FIELD_MAPPING,
      post_action_processors: postActionProcessors,
    });
  }

  // Check if any communications use email
  const hasEmailAction = sortedFollowUpCommunications.some(
    (comm) => comm.contactVia === CommunicationChannel.EMAIL,
  );

  // Required params based on action types
  const requiredParams = [WORKFLOW_TO_NUMBER_PARAM];
  if (hasEmailAction) {
    requiredParams.push(WORKFLOW_TO_EMAIL_PARAM);
  }

  const context: CampaignWorkflowContext = {
    from_numbers: fromNumbers,
    emailCampaignConfig: campaign.emailCampaignConfig,
    crmCampaignConfig: campaign.crmCampaignConfig,
    smsCampaignConfig: validatedSmsCampaignConfig,
    initialCallConfig: campaign.initialCallConfig,
  };

  return {
    referenceId: campaign.referenceId,
    label: campaign.name,
    description: campaign.description || '',
    config: {
      params_schema: {
        type: 'object',
        required: requiredParams,
        properties: {
          context: { type: 'object', description: 'Agent prompt context' },
          [WORKFLOW_TO_NUMBER_PARAM]: {
            type: 'string',
            description: 'Number being called and used for SMS',
          },
          ...(hasEmailAction && {
            [WORKFLOW_TO_EMAIL_PARAM]: {
              type: 'string',
              description: 'Email address for follow-up emails',
            },
          }),
        },
      },
      schedule: {
        timeZone,
        startDate: campaign.schedule.campaignStartDate,
        allowedTimes,
      },
      data_sources: dataSources,
      steps,
      context,
    },
    contactWindowId: campaign.contactWindowId,
  };
};
